import React, { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import MessageInput from '~ComponentsImproved/MessageInput';
import { useButton, useI18n } from '~Hooks';
import { handyBreakpoint } from '~Styles/breakpoints';
import PredefinedMessageButton from './PredefinedMessageButton';
import { BlueButton, GreenButton } from '~Styles';

const Messages = styled.div`
  display: flex;
  flex-flow: row wrap;
  justify-content: center;
  width: 100%;
  padding: 40px;
  margin-top: -40px;
  box-sizing: border-box;
  gap: 16px;

  button {
    margin: 8px;
    min-width: 152px;
    width: auto;
    padding: 0 24px;
  }

  ${handyBreakpoint} {
    padding: 16px 0;

    button {
      min-width: 0;
      min-height: 48px;
    }
  }
`;

const ButtonWrapper = styled.div`
  width: 100%;
  display: flex;
  gap: 16px;
`;

export type Message = { id: string; label: string };
interface Props {
  onSubmit: (value: Message | string) => void;
  messages: Array<Message | string>;
  hideTextarea?: boolean;
  width?: number;
  buttonToOpenTextarea?: boolean;
  value?: string | null;
  disabledMessages?: string[];
}

const PredefinedMessages: React.FunctionComponent<Props> = ({
  onSubmit,
  messages,
  hideTextarea = false,
  width,
  buttonToOpenTextarea = false,
  value,
  disabledMessages = [],
}) => {
  const { i18n } = useI18n();
  const [showTextarea, setShowTextarea] = useState(buttonToOpenTextarea ? false : !hideTextarea);
  const [message, setMessage] = useState<Message | string>('');
  const userOpenedTextarea = useRef(false);

  useEffect(() => {
    if (userOpenedTextarea.current) {
      return;
    }
    if (messages.length > 0 && buttonToOpenTextarea) {
      setShowTextarea(false);
    } else if (buttonToOpenTextarea) {
      setShowTextarea(true);
    }
  }, [messages]);

  useEffect(() => {
    value && setMessage(value);
  }, [value]);

  const messageIsSelected = (selectedMessage: string): boolean => {
    if (typeof message !== 'string') {
      return false;
    }

    return (message as string).includes(selectedMessage);
  };

  const setSelectedMessage = (selectedMessage: string) => {
    if (typeof message !== 'string') {
      setMessage(selectedMessage);
    }

    // Set a toggleable comma separated list of messages
    const isSelected = messageIsSelected(selectedMessage);
    setMessage((prevMessage) => {
      // Remove message
      if (isSelected) {
        // Before removing the message, match comma separation.
        // Escape selectedMessage so special regex chars (*, +, ?, etc.) are treated as literals.
        const escaped = selectedMessage.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const regex = new RegExp(`(,?.?)(${escaped})(,?.?)`);
        const match = (prevMessage as string).match(regex);
        // Remove whitespace and commas as nessecary
        const replacement = match && match[1].length > 0 && match[3].length > 0 ? ', ' : '';

        return (prevMessage as string).replace(regex, replacement);
      }

      // Add message comma separated
      return `${prevMessage && prevMessage + ', '}${selectedMessage}`;
    });
  };

  const sendButton = useButton({
    onClick: () => onSubmit(message),
  });

  const customMessageButton = useButton({
    onClick: () => {
      userOpenedTextarea.current = true;
      setTimeout(() => {
        setShowTextarea(true);
      }, 10);
    },
  });

  const showingMessages = messages.length > 0 && buttonToOpenTextarea ? !showTextarea : true;

  return (
    <>
      {showingMessages && (
        <Messages>
          {messages.map((m, i) => {
            const messageAsString = typeof m === 'string' ? m : m.label;
            const isDisabled = disabledMessages.includes(messageAsString);

            return (
              <PredefinedMessageButton
                key={messageAsString}
                message={messageAsString}
                isDisabled={isDisabled}
                setSelectedMessage={(mess) =>
                  hideTextarea ? onSubmit(m) : setSelectedMessage(mess)
                }
                isSelected={messageIsSelected(messageAsString)}
              />
            );
          })}
        </Messages>
      )}
      {buttonToOpenTextarea && !showTextarea && (
        <ButtonWrapper>
          <BlueButton
            {...customMessageButton}
            style={{
              flex: 2,
            }}>
            {i18n('General.CustomMessage')}
          </BlueButton>
          <GreenButton {...sendButton}>{i18n('TM.Save')}</GreenButton>
        </ButtonWrapper>
      )}
      {showTextarea && (
        <MessageInput
          autoFocus={(buttonToOpenTextarea && !showingMessages) || !messages.length}
          placeHolder={i18n('General.MessagePlaceholder')}
          value={typeof message === 'string' ? message : message.label}
          onSubmit={(value: string) => onSubmit(value)}
          onInput={(value: string) => setMessage(value)}
          width={width ? `${width}px` : undefined}
          acceptEmpty
        />
      )}
    </>
  );
};

export default PredefinedMessages;
